/**
 * Open-in-new-tab action button for the file-preview dialog header.
 *
 * Self-hides when the source can't supply an external URL (artifacts
 * and inline sources). For URL-backed sources the external URL is the
 * source URL itself.
 */
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import type { FileSource } from "../types";

export function OpenExternalAction({ source }: { source: FileSource }) {
  if (!source.capabilities.canOpenExternal) return null;

  const handleOpen = () => {
    /**
     * Open the tab SYNCHRONOUSLY, inside the click gesture. Resolving the
     * external URL is async (it mints a short-lived access token), and mobile
     * Safari / Chrome block `window.open` once it's called after an `await`
     * because it no longer counts as user-initiated. So we open a blank tab
     * now and navigate it to the resolved URL when the token is ready.
     *
     * `noopener` is intentionally omitted here: it makes `window.open` return
     * null in several browsers, and we need the handle to set `location`. The
     * destination is our own same-origin API, so there's no cross-origin
     * opener risk; we still null the opener defensively.
     */
    const newWindow = window.open("about:blank", "_blank");
    if (newWindow) newWindow.opener = null;

    void (async () => {
      try {
        const url = source.resolveExternalUrl
          ? await source.resolveExternalUrl()
          : null;
        if (!url) {
          newWindow?.close();
          toast.error("No external URL available");
          return;
        }
        if (newWindow) {
          newWindow.location.href = url;
        } else {
          // Popup was blocked before we could get a handle — try a direct open
          // as a fallback (may also be blocked, but it's the best we can do).
          window.open(url, "_blank", "noopener,noreferrer");
        }
      } catch (err) {
        newWindow?.close();
        toast.error(err instanceof Error ? err.message : "Open failed");
      }
    })();
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleOpen}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10 transition-colors"
          aria-label={`Open ${source.filename} in new tab`}
        >
          <ExternalLink className="size-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p>Open in new tab</p>
      </TooltipContent>
    </Tooltip>
  );
}
